import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import atlasPlates from '../data/atlas_plates.json';
import { resolveAtlasArcheo } from '../data/atlas_concordance';
import { 
  Image as ImageIcon, X, Info, CheckCircle2, 
  ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2, 
  RefreshCw, ArrowLeft, Move 
} from 'lucide-react';

export default function GravuresToc() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Résolution robuste d'une planche à partir du paramètre URL (chiffre entier ou romain)
  const resolvePlateFromParam = (param, figParam = '', origPl = '', origFig = '') => {
    if (!param) return null;
    let targetPlateNum = parseInt(param, 10);
    let targetFig = figParam;
    let note = '';

    // Si on a déjà un paramètre orig_pl (planche déjà résolue vers l'éd. 1883 par FormattedCommentary),
    // on évite de re-mapper récursivement et on charge directement la planche 1883 ciblée !
    if (origPl && !isNaN(targetPlateNum) && targetPlateNum >= 1 && targetPlateNum <= 93) {
      const found = atlasPlates.find((x) => x.plate === targetPlateNum);
      if (found) {
        note = `Planche citée par Fillion dans l'éd. 1886 : pl. ${origPl}${origFig ? `, fig. ${origFig}` : ''} → Planche ${found.roman}${targetFig ? `, fig. ${targetFig}` : ''} dans l'éd. 1883 numérisée`;
        return {
          ...found,
          targetFig: targetFig || '',
          concordanceNote: note
        };
      }
    }

    const resolved = resolveAtlasArcheo(param, figParam);
    if (!resolved) return null;
    const found = atlasPlates.find((x) => x.plate === resolved.plate);
    if (found) {
      note = resolved.note;
      if (!note && origPl) {
        note = `Planche citée par Fillion dans l'éd. 1886 : pl. ${origPl}${origFig ? `, fig. ${origFig}` : ''} → Planche ${found.roman}${resolved.fig ? `, fig. ${resolved.fig}` : ''} dans l'éd. 1883 numérisée`;
      }
      return {
        ...found,
        targetFig: resolved.fig || figParam,
        concordanceNote: note
      };
    }
    return null;
  };

  const initialResolved = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const rawPlanche = params.get('planche');
    const rawFig = params.get('fig') || params.get('figure') || '';
    const origPl = params.get('orig_pl') || '';
    const origFig = params.get('orig_fig') || '';
    return resolvePlateFromParam(rawPlanche, rawFig, origPl, origFig);
  }, []);

  const [selected, setSelected] = useState(initialResolved);
  const [activeFig, setActiveFig] = useState(initialResolved ? (initialResolved.targetFig || '') : '');
  const [concordanceNote, setConcordanceNote] = useState(initialResolved ? (initialResolved.concordanceNote || '') : '');

  // Visionneuse interactive : zoom, rotation, pan et plein écran
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Détecte si l'utilisateur provient directement de la lecture du texte biblique
  const fromBible = useMemo(() => {
    return Boolean(
      location.state?.fromBible ||
      searchParams.get('from_bible') === '1' ||
      (searchParams.get('planche') && document.referrer && !document.referrer.includes('/gravures'))
    );
  }, [location.state, searchParams]);

  const resetTransforms = () => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  // Synchronisation avec l'état plein écran du navigateur
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Défilement automatique vers la figure ciblée dans la liste latérale
  useEffect(() => {
    if (activeFig && selected) {
      const timer = setTimeout(() => {
        const el = document.querySelector('[data-figure-target="true"]');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [activeFig, selected]);

  // Groupe les planches consécutives portant le même titre de section
  const sections = useMemo(() => {
    const out = [];
    atlasPlates.forEach((p) => {
      const last = out[out.length - 1];
      if (last && last.title === p.title) last.plates.push(p);
      else out.push({ title: p.title, plates: [p] });
    });
    return out;
  }, []);

  // Ouverture directe via ?planche=... (entier ou romain) et mise à jour de la figure ciblée
  useEffect(() => {
    const rawPlanche = searchParams.get('planche');
    const rawFig = searchParams.get('fig') || searchParams.get('figure') || '';
    const origPl = searchParams.get('orig_pl') || '';
    const origFig = searchParams.get('orig_fig') || '';
    if (rawPlanche) {
      const p = resolvePlateFromParam(rawPlanche, rawFig, origPl, origFig);
      if (p) {
        setSelected(p);
        setActiveFig(p.targetFig || rawFig);
        setConcordanceNote(p.concordanceNote || '');
        resetTransforms();
      }
    }
  }, [searchParams]);

  const openPlate = (p) => {
    setSelected(p);
    setActiveFig('');
    setConcordanceNote('');
    resetTransforms();
    setSearchParams({ planche: p.plate }, { replace: true });
  };

  // Fermeture et retour : revient DIRECTEMENT au texte si l'utilisateur venait de la Bible
  const handleBack = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }

    if (fromBible) {
      if (location.state?.returnUrl) {
        navigate(location.state.returnUrl);
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/bible');
      }
    } else {
      setSelected(null);
      setActiveFig('');
      setConcordanceNote('');
      resetTransforms();
      setSearchParams({}, { replace: true });
    }
  }, [fromBible, location.state, navigate, setSearchParams]);

  // Gestion de la touche Echap
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        } else if (selected) {
          handleBack();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selected, handleBack]);

  // Contrôles de zoom et rotation
  const zoomIn = () => setZoom((z) => Math.min(+(z + 0.3).toFixed(2), 4));
  const zoomOut = () => setZoom((z) => Math.max(+(z - 0.3).toFixed(2), 0.7));
  const rotateClockwise = () => setRotation((r) => (r + 90) % 360);

  const toggleFullscreen = () => {
    if (!modalRef.current) return;
    if (!document.fullscreenElement) {
      modalRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoom((z) => Math.min(+(z + 0.15).toFixed(2), 4));
    } else {
      setZoom((z) => Math.max(+(z - 0.15).toFixed(2), 0.7));
    }
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  // Écouteur global pour relâcher le glisser même si la souris sort du conteneur
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) setIsDragging(false);
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging]);

  const handleDoubleClick = () => {
    if (zoom > 1.2) {
      resetTransforms();
    } else {
      setZoom(2.2);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8 animate-fade-in">
      <div className="flex items-center space-x-3 border-b border-border pb-4">
        <ImageIcon className="h-8 w-8 text-rubric-red" />
        <h1 className="text-3xl font-heading font-bold text-ink-primary">
          Atlas archéologique de la Bible
        </h1>
      </div>
      <p className="text-ink-secondary">
        {atlasPlates.length} planches de l'Atlas archéologique de la Bible (Fillion). Cliquez sur une planche pour l'agrandir et lire sa légende.
      </p>

      {sections.map((s, i) => (
        <section key={i} className="space-y-3">
          <h2 className="text-lg font-heading font-semibold text-ink-primary border-b border-border/50 pb-1">
            {s.title}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {s.plates.map((p) => (
              <button
                type="button"
                key={p.plate}
                onClick={() => openPlate(p)}
                className="flex flex-col text-left p-2 rounded-lg bg-paper border border-border hover:border-gold-leaf/50 shadow-sm transition hover:shadow-md cursor-pointer"
              >
                <div className="w-full h-44 bg-black/5 rounded overflow-hidden flex items-center justify-center">
                  <img src={p.thumb} alt={`Planche ${p.roman}`} loading="lazy" className="w-full h-full object-contain" />
                </div>
                <span className="mt-2 text-sm font-semibold text-ink-primary">
                  Planche <span style={{ fontVariant: 'small-caps' }}>{p.roman}</span>
                </span>
                <span className="text-xs text-ink-secondary line-clamp-1">{p.title}</span>
              </button>
            ))}
          </div>
        </section>
      ))}

      {/* Modal Horizontal sur ordinateur & Plein écran interactif avec Zoom */}
      {selected && (
        <div 
          ref={modalRef}
          className={`fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center ${
            isFullscreen ? 'p-0' : 'p-2 sm:p-4 md:p-6'
          } overflow-hidden animate-fade-in`} 
          onClick={handleBack}
        >
          <div 
            className={`w-full max-w-7xl mx-auto flex flex-col ${
              isFullscreen ? 'h-screen max-w-none rounded-none border-none' : 'h-[92vh] rounded-xl border border-border'
            } bg-paper shadow-2xl overflow-hidden`} 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barre de contrôle supérieure */}
            <div className="flex items-center justify-between px-3 sm:px-5 py-2 sm:py-3 border-b border-border bg-paper-dark/30 shrink-0 gap-2">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                {fromBible ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rubric-red text-white hover:bg-rubric-red/90 text-xs sm:text-sm font-medium transition shadow-xs cursor-pointer shrink-0"
                    title="Revenir directement au texte biblique"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="font-semibold">Retour au texte</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-paper-dark hover:bg-paper-dark/80 text-ink-primary text-xs sm:text-sm font-medium transition border border-border cursor-pointer shrink-0"
                    title="Fermer et retourner aux gravures"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>Toutes les planches</span>
                  </button>
                )}

                <div className="min-w-0 truncate">
                  <h3 className="text-base sm:text-lg font-heading font-bold text-ink-primary flex items-center gap-2 truncate">
                    <span>Planche <span style={{ fontVariant: 'small-caps' }}>{selected.roman}</span></span>
                    <span className="text-xs font-sans font-normal text-ink-secondary hidden sm:inline">({selected.plate} / {atlasPlates.length})</span>
                  </h3>
                  <p className="text-xs text-rubric-red truncate hidden sm:block">{selected.title}</p>
                </div>
              </div>

              {/* Outils de zoom, rotation et plein écran */}
              <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={rotateClockwise}
                  title="Pivoter de 90° (horizontal/vertical)"
                  className="p-1.5 sm:p-2 rounded-lg bg-paper-dark hover:bg-amber-100 dark:hover:bg-amber-950/60 text-ink-primary transition flex items-center gap-1 text-xs font-medium border border-border/60 cursor-pointer"
                >
                  <RotateCw className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                  <span className="hidden md:inline">Pivoter 90°</span>
                </button>
                <button
                  type="button"
                  onClick={zoomOut}
                  title="Dézoomer (-)"
                  className="p-1.5 sm:p-2 rounded-lg bg-paper-dark hover:bg-black/10 dark:hover:bg-white/10 text-ink-primary transition border border-border/60 cursor-pointer"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <span className="text-xs font-mono px-1.5 py-1 text-ink-secondary min-w-[3rem] text-center hidden sm:inline">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={zoomIn}
                  title="Zoomer (+)"
                  className="p-1.5 sm:p-2 rounded-lg bg-paper-dark hover:bg-black/10 dark:hover:bg-white/10 text-ink-primary transition border border-border/60 cursor-pointer"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={resetTransforms}
                  title="Ajuster à l'écran / réinitialiser"
                  className="p-1.5 sm:p-2 rounded-lg bg-paper-dark hover:bg-black/10 dark:hover:bg-white/10 text-ink-primary transition border border-border/60 cursor-pointer"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                  className="p-1.5 sm:p-2 rounded-lg bg-rubric-red text-white hover:bg-rubric-red/90 font-medium transition flex items-center gap-1 text-xs shadow-xs cursor-pointer"
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  <span className="hidden md:inline">{isFullscreen ? 'Quitter' : 'Plein écran'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleBack}
                  aria-label="Fermer et retourner au texte"
                  title="Fermer"
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/60 text-ink-secondary hover:text-red-700 dark:hover:text-red-300 transition ml-1 cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Corps du modal : Dispositif horizontal sur ordinateur (lg:flex-row) */}
            <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
              {/* Zone principale : Affichage horizontal de la planche avec zoom interactif et glisser-déposer */}
              <div 
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onDoubleClick={handleDoubleClick}
                className={`lg:w-7/12 xl:w-2/3 h-1/2 lg:h-full relative flex items-center justify-center bg-stone-900 dark:bg-black/95 overflow-hidden select-none ${
                  isDragging ? 'cursor-grabbing' : zoom > 1 ? 'cursor-grab' : 'cursor-zoom-in'
                }`}
              >
                {/* Aide visuelle discrète pour le bureau */}
                <div className="absolute bottom-3 left-3 z-10 hidden sm:flex items-center gap-2 px-2.5 py-1 rounded bg-black/60 backdrop-blur-xs text-white/80 text-[11px] pointer-events-none">
                  <Move className="h-3 w-3" />
                  <span>Glisser pour naviguer · Molette pour zoomer · Double-clic pour 2x</span>
                </div>

                <div 
                  className="transition-transform duration-150 ease-out flex items-center justify-center max-w-full max-h-full p-2"
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${zoom})`,
                    transformOrigin: 'center center'
                  }}
                >
                  <img
                    src={selected.image}
                    alt={`Planche ${selected.roman} - ${selected.title}`}
                    draggable={false}
                    className="max-w-[88vw] lg:max-w-none max-h-[42vh] lg:max-h-[82vh] object-contain rounded shadow-2xl pointer-events-none"
                  />
                </div>
              </div>

              {/* Volet latéral droit : Liste des figures gravées et détails exégétiques */}
              <div className="lg:w-5/12 xl:w-1/3 h-1/2 lg:h-full flex flex-col bg-paper border-t lg:border-t-0 lg:border-l border-border overflow-hidden">
                {/* En-tête et concordance */}
                <div className="p-3 sm:p-4 border-b border-border bg-paper-dark/20 shrink-0 space-y-2">
                  <div>
                    <h4 className="font-heading font-bold text-ink-primary text-base sm:text-lg">
                      Planche <span style={{ fontVariant: 'small-caps' }}>{selected.roman}</span>
                    </h4>
                    <p className="text-xs sm:text-sm text-rubric-red font-medium">{selected.title}</p>
                  </div>

                  {concordanceNote && (
                    <div className="flex items-start gap-1.5 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 text-xs text-amber-900 dark:text-amber-200">
                      <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
                      <span><strong>Concordance historique :</strong> {concordanceNote}</span>
                    </div>
                  )}
                </div>

                {/* Liste déroulante des figures */}
                <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-2">
                  <h5 className="font-heading font-bold text-ink-primary text-xs sm:text-sm uppercase tracking-wider text-ink-secondary pb-1 border-b border-border/50 flex items-center justify-between">
                    <span>Figures de la planche</span>
                    {activeFig && (
                      <span className="text-[11px] font-sans font-medium bg-rubric-red/10 text-rubric-red px-2 py-0.5 rounded-full lowercase first-letter:uppercase">
                        fig. {activeFig} ciblée
                      </span>
                    )}
                  </h5>

                  {selected.figures && selected.figures.length > 0 ? (
                    selected.figures.map((f, idx) => {
                      const targetTokens = activeFig ? activeFig.split(/[\s,–-]+/).filter(Boolean) : [];
                      const isTarget = activeFig && (
                        f.fig === activeFig ||
                        targetTokens.includes(String(f.fig).trim()) ||
                        f.fig.startsWith(activeFig + ' ') ||
                        f.fig.includes(activeFig)
                      );
                      return (
                        <div
                          key={idx}
                          data-figure-target={isTarget ? "true" : undefined}
                          className={`p-2.5 rounded-lg border transition ${
                            isTarget
                              ? 'bg-amber-100/80 dark:bg-amber-950/70 border-amber-500 shadow-sm ring-2 ring-amber-400/50'
                              : 'bg-paper-dark/30 border-border/70 hover:border-border'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <span className={`shrink-0 font-bold px-1.5 py-0.5 rounded text-xs ${
                              isTarget ? 'bg-rubric-red text-white' : 'bg-black/10 dark:bg-white/10 text-ink-primary'
                            }`}>
                              Fig. {f.fig}
                            </span>
                            <span className="text-xs sm:text-sm text-ink-primary leading-snug">
                              {f.text}
                            </span>
                          </div>
                          {isTarget && (
                            <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-rubric-red">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <span>Figure citée dans le texte biblique</span>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-xs text-ink-secondary italic">Aucune figure détaillée pour cette planche.</p>
                  )}

                  {/* Fac-similé original de la légende */}
                  {selected.legend_image && (
                    <details className="border-t border-border/60 pt-3 group mt-4">
                      <summary className="cursor-pointer text-xs sm:text-sm font-semibold text-rubric-red hover:underline flex items-center justify-between">
                        <span>Fac-similé scanné de la légende originale</span>
                        <span className="text-xs text-ink-secondary group-open:rotate-180 transition-transform">▼</span>
                      </summary>
                      <div className="mt-2 bg-black/5 dark:bg-black/20 p-2 rounded">
                        <img
                          src={selected.legend_image}
                          alt={`Légende planche ${selected.roman}`}
                          className="w-full object-contain rounded"
                          loading="lazy"
                        />
                      </div>
                    </details>
                  )}
                </div>

                {/* Bouton de retour en pied de panneau latéral si issu de la Bible */}
                {fromBible && (
                  <div className="p-3 border-t border-border bg-paper-dark/30 shrink-0">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-rubric-red text-white hover:bg-rubric-red/90 text-sm font-medium transition shadow-xs cursor-pointer"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Retourner au texte biblique</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
